import { IPoint2d } from '@/editor/math/IPoint2d';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { OperateAbstract } from './OperateAbstract';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { normalizeAngle } from '@/editor/math/utils';
import BaseEntity from '@/editor/entities/BaseEntity';
import { Vector2 } from 'three';
import { EEntityStatus, ETransformType } from '@/editor/types/entity';
import { EColumnType, IColumnData, IWindowData } from '@/editor/data/model/model';
import { cloneDeep } from 'lodash-es';
import type { IFurniture } from '@/editor/data/model/furniture';
import { getWindowDistance } from '@/editor/utils/window';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { IAssemblyData } from '@/editor/data/assembly/assembly';
import { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { adsorbModelInfos } from '@/editor/data/multiModel/utils';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { calculateWindowDoorRulerInfos } from '@/editor/stores/utils/ruler/windowDoor';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';
import { getAllStructOutlines } from '@/editor/stores/utils/column';
import { IPureEdge } from '@/editor/types';
import { ColumnEntity } from '@/editor/entities/columnEntity';
import FurnitureEntity from '@/editor/entities/furnitureEntity';
import WindowEntity from '@/editor/entities/windowEntity';
import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { getWindowWallSeg } from '@/editor/manager/absorb/window/utils';
import { clampScaleOffset, getWindowScaleRoom } from './scaleLimit';
import type { IScaleDimension, IScaleLimit } from './scaleLimit';
import { VectorPointUtils } from '@/editor/math/VectorPointUtils';

const TRANSFORM_ID_SUFFIX = 'transform';
const ORIGIN = new Vector2(0, 0);
/** 不限制上限时的缺省区间，只保留最小尺寸兜底 */
const DEFAULT_SCALE_LIMIT: IScaleLimit = { min: BUILD_PROPERTIES.MIN_INPUT, max: Infinity };

type TSelectedData = IColumnData | IFurniture | IWindowData | IAssemblyData | IMultiModelData;

export class OperateTransform extends OperateAbstract<EEntityType> {
  private _checkedEntity: BaseEntity<EEntityType> | null | undefined = null;
  private _transformManager = modelManager.transformManager;
  private _transformType: ETransformType = ETransformType.Empty;

  // mousedown时clone一次，避免后续操作读取最新值
  private _selectedData: TSelectedData | null = null;
  // move中最后一次计算结果，completeMove时一次性提交
  private _virtualData: TSelectedData | null = null;
  private _startPoint: IPoint2d = { x: 0, y: 0 };
  private _rulerModel = new RulerModel();
  private _boxRulerExtraLines: IPureEdge[] = [];
  private _boxRulerNoIncludeSepWalls = false;

  private _virtualColumnEntity: ColumnEntity | null = null;
  private _virtualFurnitureEntity: FurnitureEntity | null = null;
  private _virtualWindowEntity: WindowEntity | null = null;
  // Assembly/MultiModel的子家具虚拟实体
  private _virtualSubEntityMap: Map<string, FurnitureEntity> = new Map();
  private _assemblySubDatas: IFurniture[] = [];
  private _hiddenEntityRef: { id: string; type: EEntityType } | null = null;
  private _hiddenFurnitureIds: string[] = [];
  // startMove的RAF回调可能晚于completeMove/clear执行，用代数使其失效
  private _startGeneration = 0;
  // mousedown时算一次即可：拖动期间墙体不变，offset又是相对_startPoint的位移
  private _scaleLimit: IScaleLimit = DEFAULT_SCALE_LIMIT;

  static _v1 = new Vector2(0, 0);
  static _v2 = new Vector2(0, 0);

  constructor() {
    super();
  }

  private get isProportionalScale() {
    return [
      ETransformType.ScaleLeftBottom,
      ETransformType.ScaleRightBottom,
      ETransformType.ScaleRightTop,
      ETransformType.ScaleLeftTop
    ].includes(this._transformType);
  }

  private get _currentOperateSelected() {
    const globalSelected = globalStores.editorStateStore.selected;
    return globalSelected.entityId ? globalSelected : null;
  }

  set checkedEntity(entity: BaseEntity<EEntityType> | null | undefined) {
    if (!entity || !this._currentOperateSelected) return;
    this._checkedEntity = entity;

    const selectedEntity = modelManager.getEntity(
      this._currentOperateSelected.entityId,
      this._currentOperateSelected.type
    );
    if (selectedEntity) {
      selectedEntity.selected = true;
    }
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  get transformType() {
    return this._transformType;
  }

  set transformType(type: ETransformType) {
    this._transformType = type;
  }

  override startMove(point: IPoint2d) {
    const generation = ++this._startGeneration;
    requestAnimationFrame(() => {
      if (generation !== this._startGeneration) return;

      this._transformManager.update({
        transformType: this._transformType,
        status: EEntityStatus.selected
      });

      this._startPoint = point;

      switch (this._currentOperateSelected?.type) {
        case EEntityType.Column:
          this._selectedData = cloneDeep(
            globalStores.structStore.getColumnData(this._currentOperateSelected.entityId) || null
          );
          break;
        case EEntityType.Furniture:
          this._selectedData = cloneDeep(
            globalStores.modelStore.getFurnitureData(this._currentOperateSelected.entityId) || null
          );
          break;
        case EEntityType.DoorWindow: {
          this._selectedData = cloneDeep(
            globalStores.modelStore.getWindowData(this._currentOperateSelected.entityId) || null
          );
          break;
        }
        case EEntityType.Assembly: {
          this._selectedData = cloneDeep(
            globalStores.assemblyStore.getAssemblyData(this._currentOperateSelected.entityId) || null
          );
          break;
        }
        case EEntityType.MultiModel: {
          this._selectedData = cloneDeep(globalStores.getDataByIdType('', EEntityType.MultiModel) || null);
          break;
        }
        default:
          this._selectedData = null;
          break;
      }

      this._scaleLimit = this._getScaleLimit();

      this.createVirtualEntities();

      this.initRuler();
      this.updateRuler(this._selectedData);
    });
  }

  move(point: IPoint2d) {
    this.moving = true;

    switch (this._transformType) {
      case ETransformType.Rotation:
        this.rotationMove(point);
        break;
      case ETransformType.ScaleRightTop:
      case ETransformType.ScaleRightBottom:
      case ETransformType.ScaleLeftTop:
      case ETransformType.ScaleLeftBottom:
      case ETransformType.ScaleLeft:
      case ETransformType.ScaleRight:
      case ETransformType.ScaleTop:
      case ETransformType.ScaleBottom:
        this.scaleMove(point);
        break;
    }

    if (this._virtualData && this._currentOperateSelected) {
      globalStores.editorStateStore.transformingData = {
        type: this._currentOperateSelected.type,
        data: this._virtualData
      };
      // 通知属性面板读取拖拽中的临时数据
      editorEvent.emit(EditorEventName.PARAM_CHANGE);
    }

    return point;
  }

  private createVirtualEntities() {
    if (!this._selectedData || !this._currentOperateSelected) return;

    const { entityId, type } = this._currentOperateSelected;

    switch (type) {
      case EEntityType.Column: {
        modelManager.getEntity(entityId, EEntityType.Column)?.setVisible(false);
        this._hiddenEntityRef = { id: entityId, type };
        this._virtualColumnEntity = new ColumnEntity(this._selectedData as IColumnData);
        break;
      }
      case EEntityType.Furniture: {
        modelManager.getEntity(entityId, EEntityType.Furniture)?.setVisible(false);
        this._hiddenEntityRef = { id: entityId, type };
        // id需与真实家具区分：家具2d渲染是按entityId注册的InstancedMesh，
        // 同id时dispose虚拟实体会把真实家具的实例一并注销
        this._virtualFurnitureEntity = new FurnitureEntity({ id: entityId + TRANSFORM_ID_SUFFIX });
        this._virtualFurnitureEntity.update(this._selectedData);
        break;
      }
      case EEntityType.DoorWindow: {
        modelManager.getEntity(entityId, EEntityType.DoorWindow)?.setVisible(false);
        this._hiddenEntityRef = { id: entityId, type };
        this._virtualWindowEntity = new WindowEntity(cloneDeep(this._selectedData as IWindowData));
        break;
      }
      case EEntityType.Assembly: {
        // 缩放对组合不生效，保持真实实体不动
        if (this._transformType !== ETransformType.Rotation) break;

        modelManager.getEntity(entityId, EEntityType.Assembly)?.setVisible(false);
        this._hiddenEntityRef = { id: entityId, type };

        this._assemblySubDatas = globalStores.assemblyOperation.getAssemblyFurnitureDatas(
          this._selectedData as IAssemblyData
        );
        for (const subData of this._assemblySubDatas) {
          const furnitureEntity = new FurnitureEntity({ id: subData.id + TRANSFORM_ID_SUFFIX });
          furnitureEntity.update(subData);
          this._virtualSubEntityMap.set(subData.id, furnitureEntity);
        }
        break;
      }
      case EEntityType.MultiModel: {
        const multiModelData = this._selectedData as IMultiModelData;
        for (const modelInfo of multiModelData.modelInfos) {
          if (modelInfo.type === EEntityType.Furniture) {
            const furnitureEntity = new FurnitureEntity({ id: modelInfo.id + TRANSFORM_ID_SUFFIX });
            furnitureEntity.update(modelInfo.data);
            this._virtualSubEntityMap.set(modelInfo.id, furnitureEntity);
            this._hiddenFurnitureIds.push(modelInfo.id);
          } else if (modelInfo.type === EEntityType.Assembly) {
            for (const subData of modelInfo.subModeldatas) {
              const furnitureEntity = new FurnitureEntity({ id: subData.id + TRANSFORM_ID_SUFFIX });
              furnitureEntity.update({ ...subData, selected: false });
              this._virtualSubEntityMap.set(subData.id, furnitureEntity);
              this._hiddenFurnitureIds.push(subData.id);
            }
          }
        }
        for (const furnitureId of this._hiddenFurnitureIds) {
          modelManager.getEntity(furnitureId, EEntityType.Furniture)?.setVisible(false);
        }
        break;
      }
    }
  }

  private updateVirtualEntities(nextData: TSelectedData) {
    switch (this._currentOperateSelected?.type) {
      case EEntityType.Column:
        this._virtualColumnEntity?.update(nextData as IColumnData);
        break;
      case EEntityType.Furniture:
        this._virtualFurnitureEntity?.update(nextData);
        break;
      case EEntityType.DoorWindow:
        this._virtualWindowEntity?.update({
          extendedWindowData: nextData as IWindowData,
          status: EEntityStatus.normal,
          selected: true
        });
        break;
      case EEntityType.Assembly: {
        if (!this._selectedData) break;

        // 与AssemblyOperation.$history_rotateAssembly同式，保证预览与提交结果一致
        const deltaRotation = nextData.rotation - this._selectedData.rotation;
        for (const subData of this._assemblySubDatas) {
          this._virtualSubEntityMap.get(subData.id)?.update({
            ...subData,
            rotation: normalizeAngle(subData.rotation + deltaRotation),
            center: Point2dUtils.rotatePoint(subData.center, this._selectedData.center, deltaRotation)
          });
        }
        break;
      }
      case EEntityType.MultiModel: {
        const originData = this._selectedData as IMultiModelData | null;
        if (!originData) break;

        const originBox = {
          center: originData.center,
          width: originData.width,
          thickness: originData.thickness,
          rotation: originData.rotation
        };
        const nextModelInfos = adsorbModelInfos(
          originData.modelInfos,
          { ...originBox, rotation: nextData.rotation },
          originBox
        );
        for (const modelInfo of nextModelInfos) {
          if (modelInfo.type === EEntityType.Furniture) {
            this._virtualSubEntityMap.get(modelInfo.id)?.update(modelInfo.data);
          } else if (modelInfo.type === EEntityType.Assembly) {
            for (const subData of modelInfo.subModeldatas) {
              this._virtualSubEntityMap.get(subData.id)?.update(subData);
            }
          }
        }
        break;
      }
    }
  }

  private _getNextRotation(center: IPoint2d, rotation: number, point: IPoint2d) {
    const startDirection = Point2dUtils.getDirection(center, this._startPoint);
    const endDirection = Point2dUtils.getDirection(center, point);
    const deltaRadian = Math.atan2(endDirection.y, endDirection.x) - Math.atan2(startDirection.y, startDirection.x);

    let angle = ((rotation + deltaRadian) / Math.PI) * 180;

    // 吸附45度位置
    const multiple45 = Math.floor(angle / 45);
    let remainder = angle - multiple45 * 45;
    if (remainder > 40) remainder = 45;
    if (remainder < 5) remainder = 0;
    angle = Math.round(multiple45 * 45 + remainder);

    const nextRotation = normalizeAngle((angle * Math.PI) / 180);

    return nextRotation;
  }

  private rotationMove(point: IPoint2d) {
    if (!this._selectedData) return;

    const { rotation, center } = this._selectedData;

    const nextRotation = this._getNextRotation(center, rotation, point);
    const newData = {
      ...this._selectedData,
      rotation: nextRotation
    };

    this._virtualData = newData;
    this.updateVirtualEntities(newData);

    this._transformManager.update({ rotation: nextRotation });
    this.updateRuler(newData);
  }

  private _getScaleLimit(): IScaleLimit {
    const sizeRange = { min: BUILD_PROPERTIES.MIN_INPUT, max: BUILD_PROPERTIES.MAX_INPUT };

    switch (this._currentOperateSelected?.type) {
      case EEntityType.Column:
        return sizeRange;
      case EEntityType.Furniture: {
        // 等比缩放时高度按同一系数联动，必须一并纳入限制
        const data = this._selectedData;
        const linkedSize = this.isProportionalScale && data && 'height' in data ? data.height : undefined;
        return { ...sizeRange, linkedSize };
      }
      case EEntityType.DoorWindow:
        // 门窗宽度上限由所在墙决定，不额外套用MAX_INPUT
        return { ...DEFAULT_SCALE_LIMIT, maxOffset: this._getDoorWindowMaxOffset() };
      default:
        return DEFAULT_SCALE_LIMIT;
    }
  }

  /** 门窗被拖动的那一端到墙端点的余量。未关联墙体时不加限制 */
  private _getDoorWindowMaxOffset(): number | undefined {
    const data = this._selectedData;
    if (!data || !('relativeWallInfo' in data)) return undefined;

    const { relativeWallInfo, center, width, rotation } = data;
    if (!relativeWallInfo.relativeWallId) return undefined;

    const seg = getWindowWallSeg(relativeWallInfo.relativeWallId, relativeWallInfo.wallType || EEntityType.Wall);
    if (!seg) return undefined;

    // 门窗只启用左右两个控制柄，拖动方向即门窗自身的朝向轴
    const sign = this._transformType === ETransformType.ScaleLeft ? -1 : 1;
    const direction = { x: Math.cos(rotation) * sign, y: Math.sin(rotation) * sign };

    return getWindowScaleRoom({ seg, center, width, direction });
  }

  private _getNextSize(center: IPoint2d, rotation: number, width: number, thickness: number, point: IPoint2d) {
    const direction = new Vector2(0, 0);
    const widthPositiveDirection = new Vector2(1, 0);
    const thicknessPositiveDirection = new Vector2(0, 1);
    switch (this._transformType) {
      case ETransformType.ScaleLeft:
        direction.set(-1, 0);
        widthPositiveDirection.set(-1, 0);
        break;
      case ETransformType.ScaleRight:
        direction.set(1, 0);
        widthPositiveDirection.set(1, 0);
        break;
      case ETransformType.ScaleTop:
        direction.set(0, 1);
        thicknessPositiveDirection.set(0, 1);
        break;
      case ETransformType.ScaleBottom:
        direction.set(0, -1);
        thicknessPositiveDirection.set(0, -1);
        break;
      case ETransformType.ScaleLeftTop:
        direction.set(-width, thickness).normalize();
        widthPositiveDirection.set(-1, 0);
        thicknessPositiveDirection.set(0, 1);
        break;
      case ETransformType.ScaleLeftBottom:
        direction.set(-width, -thickness).normalize();
        widthPositiveDirection.set(-1, 0);
        thicknessPositiveDirection.set(0, -1);
        break;
      case ETransformType.ScaleRightTop:
        direction.set(width, thickness).normalize();
        widthPositiveDirection.set(1, 0);
        thicknessPositiveDirection.set(0, 1);
        break;
      case ETransformType.ScaleRightBottom:
        direction.set(width, -thickness).normalize();
        widthPositiveDirection.set(1, 0);
        thicknessPositiveDirection.set(0, -1);
        break;
    }

    direction.rotateAround(ORIGIN, rotation);

    // 每单位offset引起的宽/厚变化量。四角拖动时direction与(width, thickness)同向，
    // 两个变化率与各自的当前值成正比，等价于一个统一的缩放系数
    const widthRate = direction.dot(widthPositiveDirection.rotateAround(ORIGIN, rotation));
    const thicknessRate = direction.dot(thicknessPositiveDirection.rotateAround(ORIGIN, rotation));

    const { linkedSize } = this._scaleLimit;
    const dimensions: IScaleDimension[] = [
      { value: width, rate: widthRate },
      { value: thickness, rate: thicknessRate }
    ];
    if (linkedSize && width > 0) {
      dimensions.push({ value: linkedSize, rate: (linkedSize / width) * widthRate });
    }

    const rawOffset = OperateTransform.computeProjectionDistance(
      VectorPointUtils.vectorToPoint(direction),
      this._startPoint,
      point
    );
    // 必须先收敛offset再取整，否则取整会把值推出限制区间
    const offset = clampScaleOffset(rawOffset, dimensions, this._scaleLimit);
    const offsetVector = direction.clone().multiplyScalar(offset);

    const nextWidth = Math.round(width + offset * widthRate);
    const nextThickness = Math.round(thickness + offset * thicknessRate);
    const nextCenter = { x: center.x + offsetVector.x / 2, y: center.y + offsetVector.y / 2 };

    return { nextWidth, nextThickness, nextCenter, offset, offsetVector };
  }

  private scaleMove(point: IPoint2d) {
    if (!this._selectedData) return;
    const { center, rotation } = this._selectedData;

    const { radius: cylinderRadius } =
      'type' in this._selectedData && this._selectedData.type === EColumnType.cylinder ? this._selectedData : {};

    let radius = 0;
    let width = 'width' in this._selectedData ? this._selectedData.width || 0 : 0;
    let thickness = 'thickness' in this._selectedData ? this._selectedData.thickness || 0 : 0;

    if (cylinderRadius !== undefined) {
      radius = cylinderRadius;
      width = radius * 2;
      thickness = radius * 2;
    }

    const {
      nextWidth,
      nextThickness,
      nextCenter,
      offset,
      offsetVector: _offsetVector
    } = this._getNextSize(center, rotation, width, thickness, point);

    if (
      this._currentOperateSelected?.type === EEntityType.Column &&
      (this._selectedData as IColumnData).type === EColumnType.cylinder
    ) {
      let radiusOffset = offset / 2;
      if (this.isProportionalScale) {
        radiusOffset /= Math.sqrt(2);
      }
      const nextRadius = Math.round(radius + radiusOffset);

      const newColumnData = {
        ...this._selectedData,
        radius: nextRadius,
        center: nextCenter
      } as IColumnData;

      this._virtualData = newColumnData;
      this.updateVirtualEntities(newColumnData);

      this._transformManager.update({
        center: nextCenter,
        width: nextRadius * 2,
        height: nextRadius * 2
      });
      this.updateRuler(newColumnData);
    } else if (
      this._currentOperateSelected?.type === EEntityType.Column ||
      this._currentOperateSelected?.type === EEntityType.Furniture ||
      this._currentOperateSelected?.type === EEntityType.DoorWindow ||
      this._currentOperateSelected?.type === EEntityType.Assembly
    ) {
      const nextData: TSelectedData = {
        ...this._selectedData,
        width: nextWidth,
        thickness: nextThickness,
        center: nextCenter
      };

      switch (this._currentOperateSelected?.type) {
        case EEntityType.Furniture: {
          // 等比缩放时同时调整家具高度
          if (this.isProportionalScale) {
            const height = (this._selectedData as IFurniture).height;
            const heightScale = Math.min(nextThickness / thickness, nextWidth / width);
            if (height && heightScale) {
              nextData.height = (this._selectedData as IFurniture).height * heightScale;
            }
          }
          break;
        }
        case EEntityType.DoorWindow: {
          const doorWindowData = nextData as IWindowData;
          doorWindowData.distance = getWindowDistance(doorWindowData);
          break;
        }
      }

      this._virtualData = nextData;
      this.updateVirtualEntities(nextData);

      this._transformManager.update({
        width: nextWidth,
        center: nextCenter,
        height: nextThickness
      });
      this.updateRuler(nextData);
    }
  }

  private initRuler() {
    this._boxRulerExtraLines = [];
    this._boxRulerNoIncludeSepWalls = false;

    switch (this._currentOperateSelected?.type) {
      case EEntityType.Column:
        this._boxRulerExtraLines = getAllStructOutlines(this._currentOperateSelected.entityId);
        this._boxRulerNoIncludeSepWalls = true;
        break;
      case EEntityType.Furniture:
        this._boxRulerExtraLines = getBoxRayDetectOutlines([this._currentOperateSelected.entityId]);
        break;
    }
  }

  private getBoxRulerData(data: TSelectedData) {
    if ('type' in data && data.type === EColumnType.cylinder) {
      return {
        center: data.center,
        width: data.radius * 2,
        thickness: data.radius * 2,
        rotation: data.rotation
      };
    }

    if ('width' in data && 'thickness' in data) {
      return {
        center: data.center,
        width: data.width,
        thickness: data.thickness,
        rotation: data.rotation
      };
    }

    return null;
  }

  private updateRuler(data: TSelectedData | null | undefined) {
    if (!data) {
      this._rulerModel.clear();
      return;
    }

    if (this._currentOperateSelected?.type === EEntityType.DoorWindow) {
      this._rulerModel.updateModel(calculateWindowDoorRulerInfos(data as IWindowData));
      return;
    }

    const boxRulerData = this.getBoxRulerData(data);
    if (!boxRulerData) {
      this._rulerModel.clear();
      return;
    }

    const rulerInfos = calculateBoxRulerInfos(boxRulerData, this._boxRulerExtraLines, this._boxRulerNoIncludeSepWalls);

    this._rulerModel.updateModel(rulerInfos);
  }

  static computeProjectionDistance(direction: IPoint2d, center: IPoint2d, point: IPoint2d): number {
    OperateTransform._v1.set(direction.x, direction.y).normalize();
    OperateTransform._v2.set(point.x - center.x, point.y - center.y);

    return OperateTransform._v1.dot(OperateTransform._v2);
  }

  private commit() {
    if (!this._virtualData || !this._selectedData || !this._currentOperateSelected) return;

    switch (this._currentOperateSelected.type) {
      case EEntityType.Column: {
        globalStores.structOperation.modifyColumn(this._virtualData.id, this._virtualData as IColumnData);
        break;
      }
      case EEntityType.Furniture: {
        globalStores.furnitureOperation.modifyFurniture(this._virtualData.id, this._virtualData);
        break;
      }
      case EEntityType.DoorWindow: {
        const doorWindowData = this._virtualData as IWindowData;
        doorWindowData.distance = getWindowDistance(doorWindowData);
        globalStores.windowDoorOperation.modifyWindowDoor(doorWindowData.id, doorWindowData);
        break;
      }
      case EEntityType.Assembly: {
        if (this._transformType === ETransformType.Rotation) {
          globalStores.assemblyOperation.rotateAssembly(this._virtualData.id, this._virtualData.rotation);
        }
        break;
      }
      case EEntityType.MultiModel: {
        const originData = this._selectedData as IMultiModelData;
        globalStores.multiModelOperation.rotateMultiModel(
          originData.modelInfos,
          originData.center,
          this._virtualData.rotation - originData.rotation
        );
        break;
      }
    }
  }

  private restoreRealEntityVisible() {
    if (this._hiddenEntityRef) {
      const { id, type } = this._hiddenEntityRef;
      this._hiddenEntityRef = null;

      switch (type) {
        case EEntityType.Column:
          modelManager.getEntity(id, EEntityType.Column)?.setVisible(true);
          break;
        case EEntityType.Furniture:
          modelManager.getEntity(id, EEntityType.Furniture)?.setVisible(true);
          break;
        case EEntityType.DoorWindow:
          modelManager.getEntity(id, EEntityType.DoorWindow)?.setVisible(true);
          break;
        case EEntityType.Assembly:
          modelManager.getEntity(id, EEntityType.Assembly)?.setVisible(true);
          break;
      }
    }

    for (const furnitureId of this._hiddenFurnitureIds) {
      modelManager.getEntity(furnitureId, EEntityType.Furniture)?.setVisible(true);
    }
    this._hiddenFurnitureIds = [];
  }

  completeMove(_point: IPoint2d) {
    try {
      if (this.moving && this._virtualData && this._selectedData) {
        this.commit();
      }
    } finally {
      // commit异常时也必须复位，否则真实实体保持隐藏、transformingData残留
      this.clear();
    }
  }

  clear() {
    this._startGeneration++;
    this.restoreRealEntityVisible();

    if (globalStores.editorStateStore.transformingData) {
      globalStores.editorStateStore.transformingData = null;
      // 复位属性面板：从store读最终值（提交路径）或原值（取消路径）
      editorEvent.emit(EditorEventName.PARAM_CHANGE);
    }

    modelManager.transformManager.update({ status: EEntityStatus.normal });

    this.checkedEntity = null;
    this.moving = false;
    this._startPoint = { x: 0, y: 0 };
    this._selectedData = null;
    this._virtualData = null;
    this._scaleLimit = DEFAULT_SCALE_LIMIT;
    this._boxRulerExtraLines = [];
    this._boxRulerNoIncludeSepWalls = false;
    this._rulerModel.clear();

    this._virtualColumnEntity?.dispose();
    this._virtualFurnitureEntity?.dispose();
    this._virtualWindowEntity?.dispose();
    this._virtualColumnEntity = null;
    this._virtualFurnitureEntity = null;
    this._virtualWindowEntity = null;
    for (const entity of this._virtualSubEntityMap.values()) {
      entity.dispose();
    }
    this._virtualSubEntityMap.clear();
    this._assemblySubDatas = [];
  }

  dispose() {
    this.clear();

    this._rulerModel.dispose();
  }
}
